import Button from "components/shared/button";
import Input from "components/shared/inputs/input";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useMemo, useState } from "react";
import { useCreateEnvironmentMutation, useUpdateEnvironmentMutation } from "store/api";
import useCustomForm from "utils/hooks/useCustomForm";
import useTranslation from "utils/hooks/useTranslation";
import ISO6391 from "iso-639-1";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableEnvironmentRow = {
  key?: number | string;
  id?: number | string;
  code?: string;
  country?: string;
  active?: string | boolean;
  langs?: unknown;
};

type EnvironmentPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  envNumeric?: number;
  lang?: string;
  mode?: "create" | "edit";
  row?: EditableEnvironmentRow | null;
  onSaved?: () => Promise<void> | void;
};

function parseLangs(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      // not a JSON array, fallback to CSV parsing
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

const EnvironmentPopup: React.FC<EnvironmentPopupProps> = ({
  isOpen,
  onClose,
  request,
  envNumeric,
  lang,
  mode = "edit",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [selectedLangs, setSelectedLangs] = useState<string[]>([]);
  const [createEnvironment] = useCreateEnvironmentMutation();
  const [updateEnvironment] = useUpdateEnvironmentMutation();
  const isEditMode = mode === "edit";

  const languageOptions = useMemo(
    () =>
      ISO6391.getAllCodes().map((code) => ({
        value: code,
        label: ISO6391.getName(code) || code,
      })),
    []
  );

  const { control, reset, getValues } = useCustomForm<{
    code: string;
    country: string;
  }>({
    defaultValues: {
      code: "",
      country: "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    setIsSaving(false);
    reset({
      code: String(row?.code ?? ""),
      country: String(row?.country ?? ""),
    });
    setIsActive(isEditMode ? String(row?.active ?? "false") === "true" : false);
    setSelectedLangs(parseLangs(row?.langs));
  }, [isOpen, isEditMode, reset, row?.active, row?.code, row?.country, row?.langs]);

  const handleSave = async () => {
    if (!request) return;

    const rowId = row?.id ?? row?.key;
    if (isEditMode && rowId == null) return;

    const { code, country } = getValues();
    const baseUrl = request.url
      .replace(/\/(?:##id##|\{id\}|:id|\d+)$/i, "")
      .replace(/[?&](?:id=##id##|id=\{id\}|id=:id)(?=&|$)/i, "")
      .replace(/[?&]$/, "")
      .replace(/\/+$/, "");
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;

    setIsSaving(true);
    try {
      const basePayload = {
        code,
        country,
        active: isActive,
        langs: selectedLangs,
      };

      const payload = isEditMode
        ? {
            ...basePayload,
            id: rowId,
          }
        : {
            ...basePayload,
            env: String(envNumeric ?? localStorage.getItem("_env_id") ?? ""),
            lang: String(lang ?? localStorage.getItem("_lang") ?? "fr"),
          };

      if (isEditMode) {
        await updateEnvironment({ url, data: payload }).unwrap();
      } else {
        await createEnvironment({ url, data: payload }).unwrap();
      }

      await onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier environnement" : "Nouvel environnement"}
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("code")} name="code" control={control} type="text" size="large" />
              </group>
            </group>

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={t("lang")}
                  name="langs"
                  type="select"
                  size="large"
                  mode="multiple"
                  value={selectedLangs}
                  options={languageOptions}
                  onChange={(values: any) => {
                    setSelectedLangs(Array.isArray(values) ? values : []);
                  }}
                  allowSearch
                  placeholder={t("lang")}
                />
              </group>
            </group>

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("country")} name="country" control={control} type="text" size="large" />
              </group>
            </group>

            <group data-gap="20">
              <group data-direction="column" data-gap="8">
                <Input
                  type="Checkbox"
                  label={t("active")}
                  checked={isActive}
                  onChange={(event: any) => setIsActive(Boolean(event?.target?.checked))}
                />
              </group>
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default EnvironmentPopup;
